/**
 * Button primitive — WP-admin-flat style. Ported from Play sc-marketing
 * `ui/button.tsx` (full translation log lives there). Standalone delta: the
 * `!text-*` important prefixes are dropped — they only existed to beat
 * wp-admin's unlayered `a` styles on LinkButton.
 *
 * ⚠ **`type` defaults to `"button"`, NOT the HTML default `"submit"`** (RND-1964,
 * 2026-08-14). A design-system button must never submit a form by accident. The
 * old implicit `submit` made every in-form Button a submit button: clicking
 * "Choose image" in the message editor submitted the whole form, which SAVED the
 * message and navigated to the list — the AssetPicker mounted and was torn down
 * in the same tick, so it read as "the Media Library opens and closes
 * immediately" and no image could ever be set. On the CREATE page it also
 * silently created an untitled message per click. Every real submit in this app
 * already passes `type="submit"` explicitly (verified: no `<form>` relied on the
 * implicit default), so this default is safe and closes the whole class.
 *
 * @example
 * <Button variant="primary" onClick={save}>Save</Button>          // type="button"
 * <Button type="submit" variant="primary">Save</Button>           // real submit
 * <LinkButton to="/marketing/segments/new" variant="primary">New segment</LinkButton>
 */
import type { ButtonHTMLAttributes } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

import { cn } from '../lib/cn';

type Variant = 'primary' | 'secondary' | 'danger';
type Size = 'xs' | 'sm' | 'md';

const VARIANT_CLASSES: Record<Variant, string> = {
  primary: 'bg-primary text-white border-primary hover:bg-primary-hover hover:border-primary-hover',
  secondary: 'bg-bg-muted text-fg border-border hover:bg-bg-hover',
  danger: 'bg-danger text-white border-danger hover:bg-danger-hover hover:border-danger-hover',
};

const SIZE_CLASSES: Record<Size, string> = {
  xs: 'py-0.5 px-2 text-xs',
  sm: 'py-0.75 px-2.5 text-sm',
  md: 'py-1.5 px-3.5 text-base',
};

const BASE =
  'inline-flex items-center gap-1.5 rounded border border-transparent no-underline cursor-pointer transition-colors duration-100 disabled:opacity-45 disabled:cursor-default disabled:pointer-events-none';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  type = 'button',
  className,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      {...props}
      type={type}
      className={cn(BASE, VARIANT_CLASSES[variant], SIZE_CLASSES[size], className)}
    >
      {children}
    </button>
  );
}

/** LinkButton — a react-router <Link> with Button visuals, for nav-as-button. */
interface LinkButtonProps extends Omit<LinkProps, 'className'> {
  variant?: Variant;
  size?: Size;
  className?: string;
}

export function LinkButton({
  variant = 'secondary',
  size = 'md',
  className,
  children,
  ...props
}: LinkButtonProps) {
  return (
    <Link {...props} className={cn(BASE, VARIANT_CLASSES[variant], SIZE_CLASSES[size], className)}>
      {children}
    </Link>
  );
}
